import { User } from "../models/auth.models";
import { userBalance } from "../utils/token.interface";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { environment } from "src/environments/environment";

dayjs.extend(customParseFormat);

type VerificationAwareUser = Pick<User, 'email_verified' | 'is_valid'> | null | undefined;

/**
 * Returns true when a logged-in user is still blocked by
 * email verification or account validation.
 *
 * - Regulated (email/password) users: blocked when email is not verified.
 * - Self-custody (wallet) users: blocked when email is not verified OR account is not valid.
 */
export function isVerificationBlockedUser(user: VerificationAwareUser, regulated: boolean): boolean {
    if (!user) return false;

    // Both regulated and self-custody users must have a verified email
    if (user.email_verified === false) return true;

    // is_valid is an additional gate for self-custody users only
    if (regulated === false && user.is_valid === false) return true;

    return false;
}

/**
 * Verified-only APIs should not be called for unverified self-custody users.
 */
export function canAccessVerifiedUserApis(user: VerificationAwareUser, regulated: boolean): boolean {
    if (!user) {
        return false;
    }

    return !isVerificationBlockedUser(user, regulated);
}

/**
 * Withdrawals are only available to custodial (regulated) users who have been
 * provisioned a Fireblocks vault — `regulated` alone can be true for a user
 * who hasn't been provisioned yet, so both signals are required.
 */
export function canWithdrawFunds(user: Pick<User, 'fire_block_address'> | null | undefined, regulated: boolean): boolean {
    return regulated && !!user?.fire_block_address;
}

/**
 * Checks if user is verified and valid.
 * If user is not verified or valid, it will open the verification modal.
 * @param user - User object.
 * @param verificationModal - Bootstrap modal object.
 * @returns {boolean} - True if user is verified and valid, false otherwise.
 */
export function isUserVerifiedAndValid(user: any, verificationModal: any, regulated: boolean): boolean {
    if (isVerificationBlockedUser(user, regulated)) {
        verificationModal?.show();
        return false;
    }
    return true;
}

/**
 * Handles array itrator
 * @param value
 * @param action
 * @param callBack
 */
export async function handleArrayItrator(value: Array<{ [key: string]: any }>, action: string[], callBack: (result: any) => any) {
    let overLoppedvalues: any
    overLoopValue([value, action], async (status: boolean) => {
        if (status) {
            overLoppedvalues = await customizeArryaIteration(value, action)
            callBack(overLoppedvalues);
            return ''
        } else {
            return "value isn't an array!!.Kindly check your values. "
        }
    })

}

/**
 * Checks valueis array
 * @param value
 * @returns
 */
function checkValueisArray(value: any) {
    return Array.isArray(value)
}

/**
 * Overs loop value
 * @param request
 * @param callBack
 */
function overLoopValue(request: Array<{ [key: string]: any }> | string[], callBack: (status: boolean) => any) {
    let result: boolean = false
    request.forEach((res: any) => {
        if (checkValueisArray(res)) {
            result = true
        }
    })
    callBack(result)
}

/**
 * Customizes arrya iteration
 * @param requestedArray
 * @param iterationKey
 */
function customizeArryaIteration(requestedArray: any, iterationKey: string[]) {
    let result: any = []
    requestedArray.forEach((element: any) => {
        for (const key of iterationKey) {
            if (Object.prototype.hasOwnProperty.call(element, key)) {
                result.push(element[key])
            }
        }
    });
    return result
}

/**
 * Gets the respected key values
 * @param value
 * @param givenRequest
 */
async function getTheRespectedKeyValues(value: { [key: string]: any }, givenRequest: any) {
    let result = []
    for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
            result.push(givenRequest[key])
        }
    }
    return result;
}

export function handleStoragefeeCalculation(storageFeeType: string, data: { [key: string]: any }) {
    const totalCountDays = Number(data['total_count_days']) || 0;
    const toNumber = (value: any) => {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    const roundToTwo = (value: number) => Number(value.toFixed(2));

    switch (storageFeeType) {
        case 'PER_WEIGHT_PER_YEAR': {
            const perWeightAmount = toNumber(data['per_weight_amount']);
            const weight = toNumber(data['weight']);
            return roundToTwo(perWeightAmount * weight * totalCountDays);
        }
        case 'FIXED_AMOUNT_PER_YEAR': {
            const fixedAmount = toNumber(data['fixed_amount']);
            const rawQuantity = data['quantity'];
            const parsedQuantity = toNumber(rawQuantity);
            const quantity = parsedQuantity > 0 ? parsedQuantity : 1;
            return roundToTwo(fixedAmount * quantity * totalCountDays);
        }
        case 'PERCENTAGE_OF_APPRAISED_VALUE_PER_YEAR':
        default: {
            const appraisalValue = toNumber(data['appraisal_value']);
            const overallPercentage = data['percentage'] ? toNumber(data['percentage']) / 100 : 0;
            return roundToTwo(appraisalValue * overallPercentage * totalCountDays);
        }
    }
}

/**
 * Resolves the per-bottle quantity multiplier for FIXED_AMOUNT_PER_YEAR storage fees.
 *
 * The admin-configured fixed fee is priced *per bottle per year*, so a wine "case" NFT must be
 * multiplied by the number of bottles it represents. Resolution order (wine only):
 *   1. `Bottles-Per-Case` attribute — the authoritative bottle count.
 *   2. Leading number of `CaseFormat` (e.g. "6x75cl" -> 6) — fallback when BPC is missing.
 *   3. `1` — safe default.
 * Every non-wine category is a single unit and always resolves to 1.
 *
 * The legacy `Quantity` attribute is intentionally NOT consulted here — it was stale ("1") on
 * multi-bottle cases and would under-charge/under-report. This helper is the single source of
 * truth shared by the single-NFT flow, the bulk panel, and (mirrored) the backend inventory calc.
 */
export function resolveFixedStorageQuantity(nft: any, category: string = ''): number {
    if (String(category || '').trim().toLowerCase() !== 'wine') return 1;

    const readAttr = (key: string): string => {
        const wanted = key.toLowerCase();
        const found = (nft?.attributes || []).find(
            (a: any) => String(a?.key || '').trim().toLowerCase() === wanted
        );
        return String(found?.value ?? '').trim();
    };
    // Strict numeric parse: rejects unit-carrying values like "75cl" (a malformed CaseFormat with
    // no "Nx" prefix) so they fall through to the safe default instead of parsing as a bottle count.
    const toPositiveQuantity = (raw: string): number | null => {
        const num = Number(String(raw ?? '').trim());
        return Number.isFinite(num) && num > 0 ? num : null;
    };

    const bottlesPerCase = toPositiveQuantity(readAttr('bottles-per-case'));
    if (bottlesPerCase) return bottlesPerCase;

    const caseFormat = readAttr('caseformat') || readAttr('case format');
    const caseFormatQuantity = toPositiveQuantity(caseFormat.split(/x/i)[0]);
    if (caseFormatQuantity) return caseFormatQuantity;

    return 1;
}

/**
* Params loan detail component
* @param userAmountCheck * @param [balance]  * @param [decimal]  * @param [amount]
* @param callBack isHave ==> ite return the user have enough amount or not.
*/
export async function _handleInSufficientBalance(userAmountCheck: Partial<userBalance>) {
    const { decimal, balance, amount } = userAmountCheck;
    let ishaveSuffAmnt = true
    if (Number(balance) < Number(amount)) ishaveSuffAmnt = false
    return ishaveSuffAmnt
}

export const FOUR_CORNER_WINE_URL = "https://v22-services.hub.wine/xml/4CW-REALWORLD-4EB84B"

export interface StorageFeeConfigCondition {
    category: string;
    location: string;
}

export const STORAGE_FEE_CATEGORY = ['wine', 'gold', 'silver', 'platinum', 'palladium']

/** Digital Assets (e.g. BTC) — held in a multisig wallet, valued from the live BTC price feed.
 *  The Category trait is admin-entered, so both "Digital asset" and "Digital Assets" match, in any case. */
const DIGITAL_ASSET_CATEGORY_PATTERN = /^digital assets?$/i;

/** Bitcoin address forms we link: bech32 (bc1…) and legacy base58 (1…/3…). */
const BTC_ADDRESS_PATTERN = /^(bc1[a-z0-9]{25,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/;

/**
 * Explorer link for a Digital Asset's Bitcoin wallet address, or null when the value is not a
 * Bitcoin address — an unexpected attribute value can then never be rendered as a link.
 *
 * @param address - the NFT's "Wallet Address" attribute value
 * @returns the explorer URL, or null when the address is not a valid Bitcoin address
 */
export function getBtcExplorerUrl(address: unknown): string | null {
    const value = String(address ?? '').trim();
    const normalized = value.toLowerCase().startsWith('bc1') ? value.toLowerCase() : value;
    if (!BTC_ADDRESS_PATTERN.test(normalized)) return null;
    // Per-environment base URL, so the explorer can differ per environment without a code change.
    const base = environment.BTC_EXPLORER_URL;
    return base ? `${base.endsWith('/') ? base : `${base}/`}${normalized}` : null;
}

/**
 * Shortened form of a wallet address for tight layouts (first 8 and last 6 characters), e.g.
 * `bc1q2dzk...nd3uts`. Values short enough to fit are returned unchanged.
 *
 * @param address - the full wallet address
 * @param start - characters kept from the start (default 8)
 * @param end - characters kept from the end (default 6)
 * @returns the shortened address, or '' when there is no value
 */
export function shortenWalletAddress(address: unknown, start = 8, end = 6): string {
    const value = String(address ?? '').trim();
    return value.length > start + end + 3 ? `${value.slice(0, start)}...${value.slice(-end)}` : value;
}

/**
 * Whether an NFT belongs to the Digital Assets category.
 * @param attributesOrCategory - the NFT's attributes array, or a category string
 * @returns true for Digital Assets (case-insensitive)
 */
export function isDigitalAssetCategory(attributesOrCategory: Array<{ key?: string; value?: unknown }> | string | null | undefined): boolean {
    const category = Array.isArray(attributesOrCategory)
        ? attributesOrCategory.find((attribute) => String(attribute?.key ?? '').trim().toLowerCase() === 'category' && String(attribute?.value ?? '').trim() !== '')?.value
        : attributesOrCategory;
    return DIGITAL_ASSET_CATEGORY_PATTERN.test(String(category ?? '').trim());
}

/** Days-before-lapse window that counts custody as "due soon" — single source of truth
 *  shared by the wallet grid/banner, the collection chooser, and the bulk storage-fee panel. */
export const STORAGE_FEE_DUE_SOON_DAYS = 7;

/** Most distinct custody-paid-until strings parseCustodyPaidUntilDate remembers; beyond this the oldest is dropped. */
export const CUSTODY_DATE_CACHE_LIMIT = 500;

/** One remembered parse: the instant, and the browser's UTC offset at that instant when it was parsed. */
interface CustodyDateCacheEntry {
    epochMs: number;
    offsetMinutes: number;
}

/**
 * Memo for parseCustodyPaidUntilDate, keyed by the trimmed input; null records a string that does not parse.
 * Holds primitives only, so a Dayjs handed to one caller is never shared with another.
 * A Map keeps insertion order, so its first key is always the oldest entry (FIFO eviction).
 */
const custodyDateCache = new Map<string, CustodyDateCacheEntry | null>();

/**
 * Parses a raw CustodyPaidUntil attribute value — single source of truth shared by the wallet
 * listing, nft-detail, the bulk storage-fee panel and the storage-fee pill.
 *
 * Tries the known unambiguous formats strictly first (so 03-09-2026 is not silently read as a
 * different day) before falling back to the loose parser.
 *
 * Results are remembered per trimmed string because templates call this on every change-detection
 * pass. The parse never reads the clock ("now" is always taken by the caller), so a remembered
 * result stays correct. The strict formats mean local midnight, so a remembered instant is reused
 * only while the browser's time zone still gives the same UTC offset at that instant; after a
 * time-zone change the string is parsed again, exactly as it would have been without the memo.
 *
 * @param value - Raw custody-paid-until string.
 * @returns The parsed date, or null when it is missing or cannot be parsed. Every call returns a new Dayjs.
 */
export function parseCustodyPaidUntilDate(value: string | null | undefined): Dayjs | null {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return null;

    if (custodyDateCache.has(normalizedValue)) {
        const cached = custodyDateCache.get(normalizedValue) ?? null;
        if (cached === null) return null;
        if (localUtcOffsetAt(cached.epochMs) === cached.offsetMinutes) return dayjs(cached.epochMs);
        // The time zone changed since this string was parsed: parse it again below.
    }

    const strictFormats = ['MM-DD-YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', 'DD-MM-YYYY', 'DD/MM/YYYY'];
    const matchedFormat = strictFormats.find((format) => dayjs(normalizedValue, format, true).isValid());
    const parsedDate = matchedFormat ? dayjs(normalizedValue, matchedFormat, true) : dayjs(normalizedValue);
    const result = parsedDate.isValid() ? parsedDate : null;
    rememberCustodyDate(normalizedValue, result);
    return result;
}

/** The browser's offset from UTC in minutes at the given instant, as Date.getTimezoneOffset reports it. */
function localUtcOffsetAt(epochMs: number): number {
    return new Date(epochMs).getTimezoneOffset();
}

/** Stores a parse result as primitives, dropping the oldest entry once the memo is full. */
function rememberCustodyDate(key: string, parsed: Dayjs | null): void {
    if (!custodyDateCache.has(key) && custodyDateCache.size >= CUSTODY_DATE_CACHE_LIMIT) {
        const oldestKey = custodyDateCache.keys().next().value;
        if (oldestKey !== undefined) custodyDateCache.delete(oldestKey);
    }
    const epochMs = parsed ? parsed.valueOf() : null;
    custodyDateCache.set(key, epochMs === null ? null : { epochMs, offsetMinutes: localUtcOffsetAt(epochMs) });
}

/** Empties the memo. For specs; app code never needs it because a remembered parse does not go stale. */
export function clearCustodyDateCache(): void {
    custodyDateCache.clear();
}

/** Number of remembered strings. For specs. */
export function custodyDateCacheSize(): number {
    return custodyDateCache.size;
}

/** Standard custody term a storage-fee payment buys by default, in months.
 *  The bulk panel opens on today + this term rather than on the earliest selectable date:
 *  defaulting to the minimum (tomorrow) buys ONE day of custody, which drops the asset straight
 *  back inside the STORAGE_FEE_DUE_SOON_DAYS window and reads to the customer as a failed payment.
 *  12 matches the rate model — every storage-fee type is a *_PER_YEAR rate and
 *  handleStoragefeeCalculation prices a term as rate x (days / 365). */
export const DEFAULT_STORAGE_TERM_MONTHS = 12;

/**
 * Checks whether storage-fee module must be enabled for an NFT.
 *
 * Condition:
 * - NFT attribute  must exist in admin-configured storage fees
 * - NFT attribute  must exist in admin-configured storage fees
 * - The match is evaluated as an exact category+location pair
 *
 * Note: the admin-configured-storage-fees list this is checked against carries no fee
 * amount (only category+location) — the actual fee amount lives on the separate
 * per-location /storage-fees lookup. Callers that need to know whether the resolved
 * amount is actually zero must check that lookup themselves; this function only tells
 * you whether a storage-fee row exists at all for the pair.
 */
export function isStorageFeeEnabledForCategoryAndLocation(configuredFees: StorageFeeConfigCondition[] = [], category: string = '', location: string = ''): boolean {
    const normalizedCategory = String(category || '').trim().toLowerCase();
    const normalizedLocation = String(location || '').trim().toLowerCase();

    if (!normalizedCategory || !normalizedLocation || !configuredFees.length) return false;

    return configuredFees.some((fee) => {
        return String(fee?.category || '').trim().toLowerCase() === normalizedCategory
            && String(fee?.location || '').trim().toLowerCase() === normalizedLocation;
    });
}

/**
 * Routes that render the full-screen asset onboarding flow, where the header nav and the
 * footer's back-to-top are suppressed.
 *
 * `/deposit-asset` is the redesigned 3-screen deposit flow; `/onboardassets` is the older
 * wizard it replaces, kept routable while both are live. The comparison strips any query
 * string so entry points that tag their source (`?from=dashboard`) are still recognised.
 */
export function isAssetOnboardingRoute(url: string = ''): boolean {
    const path = String(url || '').split('?')[0].split('#')[0];
    return path === '/deposit-asset' || path === '/onboardassets';
}
